import type { BankAccountInput, BankAccountUpdate } from '@/types/banking'
import type { ManualAccountType } from './manualBanks.utils'

/**
 * What one save of the manual bank form writes. Pure, so the rename / update /
 * create / delete decisions are tested without a dialog.
 */

/** One account row as the bank form submits it. */
export interface ManualAccountDraft {
  /** Present for an existing account, absent for a row added in the form. */
  id?: string
  name: string
  account_type: ManualAccountType
  account_number: string
  facility_id: string
}

/**
 * An existing account as the bank form was seeded with it. Its type may be
 * missing when the stored value is outside the enum; the form then asks for one.
 */
export interface SeededManualAccount extends Omit<ManualAccountDraft, 'id' | 'account_type'> {
  id: string
  account_type: ManualAccountType | undefined
}

/** The writes one save of the bank form needs. */
export interface ManualBankSavePlan {
  rename: { oldName: string; newName: string } | null
  updates: Array<{ id: string; data: BankAccountUpdate }>
  creates: BankAccountInput[]
  deletedIds: string[]
  /** The bank's stored name once the plan has run. */
  finalName: string
}

/**
 * Whether the submitted name renames the bank. The form trims what it submits
 * while a stored name may carry stray whitespace, so the stored name is
 * compared trimmed: leaving the field alone is not a rename.
 *
 * @param storedName - The bank's exact stored `institution_name`
 * @param submittedName - The trimmed name the form submitted
 */
export function isRename(storedName: string, submittedName: string): boolean {
  return storedName.trim() !== submittedName
}

/**
 * Work out what saving the bank form must write.
 *
 * Only rows whose values changed are updated, so a no-op save writes nothing
 * and does not bump `updated_at` on untouched rows. Seeded accounts missing
 * from the submission are deleted. New rows are created under the bank's final
 * name — the EXACT stored name when it is not renamed, so they join the group.
 *
 * @param storedName - The bank's exact stored name
 * @param submittedName - The name submitted (trimmed)
 * @param original - The bank's accounts as the form was seeded with them
 * @param submitted - The rows submitted
 */
export function planManualBankSave(
  storedName: string,
  submittedName: string,
  original: readonly SeededManualAccount[],
  submitted: readonly ManualAccountDraft[],
): ManualBankSavePlan {
  const rename = isRename(storedName, submittedName)
    ? { oldName: storedName, newName: submittedName }
    : null
  const finalName = rename ? rename.newName : storedName
  const originalById = new Map(original.map((row) => [row.id, row]))
  const submittedIds = new Set(submitted.flatMap((row) => (row.id ? [row.id] : [])))

  const updates: ManualBankSavePlan['updates'] = []
  const creates: BankAccountInput[] = []
  for (const row of submitted) {
    const before = row.id ? originalById.get(row.id) : undefined
    if (!row.id || !before) {
      creates.push({
        name: row.name,
        institution_name: finalName,
        account_type: row.account_type,
        account_number: row.account_number,
        facility_id: row.facility_id,
      })
      continue
    }
    if (draftChanged(before, row)) {
      updates.push({
        id: row.id,
        data: {
          name: row.name,
          account_type: row.account_type,
          account_number: row.account_number,
          facility_id: row.facility_id,
        },
      })
    }
  }

  return {
    rename,
    updates,
    creates,
    deletedIds: [...originalById.keys()].filter((id) => !submittedIds.has(id)),
    finalName,
  }
}

/** Whether a plan has nothing to write. */
export function isEmptySavePlan(plan: ManualBankSavePlan): boolean {
  return (
    plan.rename === null &&
    plan.updates.length === 0 &&
    plan.creates.length === 0 &&
    plan.deletedIds.length === 0
  )
}

/** Stored values may carry stray whitespace the form trims away; that is not a change. */
function draftChanged(before: SeededManualAccount, after: ManualAccountDraft): boolean {
  return (
    before.name.trim() !== after.name.trim() ||
    before.account_type !== after.account_type ||
    before.account_number.trim() !== after.account_number.trim() ||
    before.facility_id !== after.facility_id
  )
}
